import { useEffect, type ReactNode } from 'react';
import type { Business } from '@/types';

interface BrandingWrapperProps {
  business: Business | null;
  children: ReactNode;
}

export function BrandingWrapper({ business, children }: BrandingWrapperProps) {
  useEffect(() => {
    if (!business?.font_family) return;
    const font = business.font_family.replace(/\s+/g, '+');
    const linkId = 'qrhost-dynamic-font';
    let link = document.getElementById(linkId) as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.id = linkId;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    link.href = `https://fonts.googleapis.com/css2?family=${font}:wght@400;500;600;700;800&display=swap`;

    return () => {
      // keep link for smooth transitions
    };
  }, [business?.font_family]);

  useEffect(() => {
    if (!business) return;
    const root = document.documentElement;
    root.style.setProperty('--qr-primary', business.primary_color || '#2563EB');
    root.style.setProperty('--qr-secondary', business.secondary_color || '#1E40AF');
    root.style.setProperty('--qr-bg', business.background_color || '#0f172a');
    root.style.setProperty('--qr-surface', lighten(business.background_color || '#0f172a', 5));
    root.style.setProperty('--qr-text', business.text_color || '#f8fafc');
    root.style.setProperty('--qr-accent', business.accent_color || '#3b82f6');
  }, [business?.primary_color, business?.secondary_color, business?.background_color, business?.text_color, business?.accent_color]);

  return (
    <div
      style={{
        fontFamily: `'${business?.font_family || 'Inter'}', -apple-system, sans-serif`,
        minHeight: '100vh',
      }}
    >
      {children}
    </div>
  );
}

function lighten(hex: string, percent: number): string {
  try {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = Math.min(255, (num >> 16) + amt);
    const G = Math.min(255, ((num >> 8) & 0x00ff) + amt);
    const B = Math.min(255, (num & 0x0000ff) + amt);
    return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
  } catch {
    return '#1e293b';
  }
}
